import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import hrLocale from "@fullcalendar/core/locales/hr";
import api from "common/api";
import AppointmentModal from "./AppointmentModal";
import { useAuth } from "authContext";
import { ICourse } from "common/types";
import NewAppointmentModal from "./NewAppointmentModal";
import { toast } from "react-toastify";

function AppointmentsCalendar({ course }: { course: ICourse }) {
  const { userProfile } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showNewAppointmentModal, setShowNewAppointmentModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const fetchAppointments = async () => {
    try {
      const response = await api.get("/appointments/?course=" + course.id);
      setAppointments(
        response.data.map(
          (
            appointment: {
              id: any;
              start: any;
              end: any;
              course: any;
              meet_url: any;
              text: any;
              files: any;
            },
            index: number,
          ) => ({
            id: appointment.id,
            start: appointment.start,
            end: appointment.end,
            title: `${course.title} ${index + 1}/${response.data.length}`,
            extendedProps: {
              meet_url: appointment.meet_url,
              text: appointment.text,
            },
          }),
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchAppointments();
  }, [course.id]);

  const editAppointment = async (
    id: string,
    start: string,
    end: string,
    meet_url: string,
  ) => {
    try {
      await api.patch(`/appointments/${id}/`, {
        start,
        end,
        meet_url,
        course: course.id,
      });
      setShowModal(false);
      fetchAppointments();
      toast.success("Termin uspješno spremljen!");
    } catch (error) {
      console.error(error);
    }
  };

  const deleteAppointment = async (id: string) => {
    try {
      await api.delete(`/appointments/${id}/`);
      setShowModal(false);
      fetchAppointments();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDateClick = ({ date }: any) => {
    if (userProfile?.role === "STUDENT") return;
    setSelectedDate(date);
    setShowNewAppointmentModal(true);
  };

  const handleEventClick = ({ event }: any) => {
    setSelectedAppointment(event);
    setShowModal(true);
  };

  const handleEventDrop = async ({ event }: any) => {
    if (userProfile?.role === "PRINCIPAL" || userProfile?.role === "TEACHER") {
      try {
        await api.patch(`/appointments/${event.id}/`, {
          start: event.start.toISOString(),
          end: event.end.toISOString(),
          course: event.extendedProps.course,
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <div>
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
        initialView="dayGridMonth"
        events={appointments}
        eventClick={handleEventClick}
        eventDrop={handleEventDrop}
        dateClick={handleDateClick}
        editable={
          userProfile?.role === "PRINCIPAL" || userProfile?.role === "TEACHER"
        }
        headerToolbar={{
          start: "title",
          center: "",
          end: "today prev,next dayGridMonth,timeGridWeek,timeGridDay",
        }}
        locale={hrLocale}
      />
      {showModal && (
        <AppointmentModal
          show={showModal}
          onHide={() => setShowModal(false)}
          appointment={selectedAppointment}
          deleteAppointment={deleteAppointment}
          editAppointment={editAppointment}
        />
      )}
      {showNewAppointmentModal && (
        <NewAppointmentModal
          show={showNewAppointmentModal}
          onHide={() => setShowNewAppointmentModal(false)}
          selectedDate={selectedDate}
          course={course}
          refreshAppointments={fetchAppointments} // Pass the fetchAppointments function to refresh the appointments after creating a new one
        />
      )}
    </div>
  );
}

export default AppointmentsCalendar;
